import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, List, ShoppingBag, LogOut, Tag, FileText, Grid3x3, ChefHat, Printer, TrendingUp, Mic, Shield, Users, FileCheck, Utensils, Settings } from 'lucide-react';
import { getUserPermissions, isAdmin, hasAnyPermission } from '../utils/permissions';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';

export const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const allNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', requiredPermissions: ['reports.view', 'orders.view'] },
    { path: '/menu-management', icon: ChefHat, label: 'Menu Management', requiredPermissions: ['menu.view', 'menu.edit'] },
    { path: '/table-management', icon: Grid3x3, label: 'Table Management', requiredPermissions: ['tables.view', 'tables.edit'] },
    { path: '/categories', icon: List, label: 'Categories', requiredPermissions: ['categories.view', 'menu.view'] },
    { path: '/orders', icon: ShoppingBag, label: 'Orders', requiredPermissions: ['orders.view'] },
    { path: '/kitchen', icon: Utensils, label: 'Kitchen Panel', requiredPermissions: ['kitchen.view'] },
    { path: '/kitchen-settings', icon: Settings, label: 'Kitchen Settings', requiredPermissions: ['kitchen.settings', 'settings.edit'] },
    { path: '/discounts', icon: Tag, label: 'Discounts', requiredPermissions: ['menu.edit'] },
    { path: '/upsale-discounts', icon: TrendingUp, label: 'Upsale Discounts', requiredPermissions: ['menu.edit'] },
    { path: '/printer-settings', icon: Printer, label: 'Printer Settings', requiredPermissions: ['settings.view'] },
    { path: '/voice-ai-settings', icon: Mic, label: 'Voice AI Settings', requiredPermissions: ['bot.view'] },
    { path: '/roles', icon: Shield, label: 'Role Management', requiredPermissions: ['staff.manage'] },
    { path: '/staff', icon: Users, label: 'Staff Management', requiredPermissions: ['staff.view', 'staff.manage'] },
    { path: '/audit-logs', icon: FileCheck, label: 'Audit Logs', requiredPermissions: ['actionlog.view'] },
    { path: '/reports', icon: FileText, label: 'Reports', requiredPermissions: ['reports.view'] },
  ];

  // Get user permissions
  const userPermissions = getUserPermissions(user);
  const userIsAdmin = isAdmin(user);

  // Filter navigation items based on permissions
  const navItems = userIsAdmin 
    ? allNavItems 
    : allNavItems.filter(item => hasAnyPermission(userPermissions, item.requiredPermissions));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-200 shadow-sm flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600">
            {localStorage.getItem("restaurant") || "Admin"}
          </h1>
          <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600">
            WaiterMan POS
          </h1>
          <p className="text-sm text-gray-500 mt-1">Admin Panel</p>
        </div>
        <nav className="p-4 space-y-2 flex-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon size={20} />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>
        
        {/* User section at bottom */}
        <div className="p-4 border-t border-gray-200">
          <div className="mb-3 px-2">
            <p className="text-sm font-medium text-gray-900">{user?.name}</p>
            <p className="text-xs text-gray-500 capitalize">{user?.role?.replace('_', ' ')}</p>
          </div>
          <Button
            onClick={handleLogout}
            data-testid="logout-button"
            variant="outline"
            className="w-full flex items-center gap-2 justify-center"
          >
            <LogOut size={16} />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  );
};